import { useState, useMemo } from 'react';
import { getStatusStrings } from '@/services/jira-status-config';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowTrendDown,
  faArrowTrendUp,
  faBolt,
  faBullseye,
  faChartBar,
  faChartLine,
  faChevronDown,
  faChevronUp,
  faClock,
  faFire,
  faRotate,
  faShieldHalved,
  faTriangleExclamation,
  faUsers
} from '@fortawesome/free-solid-svg-icons';

const mkIcon = (def: import('@fortawesome/fontawesome-svg-core').IconDefinition) =>
  ({ className = '' }: { className?: string }) =>
    <FontAwesomeIcon icon={def} className={className} />;

// ==================== Normalized Event Interface ====================

export interface NormalizedEvent {
  id: string;
  timestamp: string;
  type: string;
  itemKey: string;
  itemSummary: string;
  author: string;
  fromStatus?: string;
  toStatus?: string;
  fromAssignee?: string;
  toAssignee?: string;
  itemType?: string;
  fieldName?: string;
}

// ==================== Indicator Types ====================

interface AggregateIndicator {
  id: string;
  name: string;
  label: string;
  severity: 'good' | 'warning' | 'critical';
  icon: React.ComponentType<{ className?: string }>;
  baseline: string;
  details?: string[];
}

// ==================== Platform Config ====================

export type PlatformType = 'linear' | 'monday' | 'trello' | 'jira';

interface PlatformConfig {
  name: string;
  accentColor: string;
  doneStatuses: string[];
  inProgressStatuses: string[];
  reworkStatuses: string[];
  forwardStatuses: string[];
}

function getJiraPlatformConfig(): PlatformConfig {
  return {
    name: 'Jira',
    accentColor: 'blue',
    doneStatuses: getStatusStrings('done'),
    inProgressStatuses: getStatusStrings('in_progress'),
    reworkStatuses: [...getStatusStrings('rework'), ...getStatusStrings('in_progress')],
    forwardStatuses: [...getStatusStrings('testing'), ...getStatusStrings('release_candidate'), ...getStatusStrings('done')],
  };
}

function getPlatformConfig(platform: PlatformType): PlatformConfig {
  if (platform === 'jira') return getJiraPlatformConfig();
  return STATIC_PLATFORM_CONFIGS[platform];
}

const STATIC_PLATFORM_CONFIGS: Record<string, PlatformConfig> = {
  linear: {
    name: 'Linear',
    accentColor: 'indigo',
    doneStatuses: ['done', 'completed', 'cancelled', 'canceled', 'archived'],
    inProgressStatuses: ['in progress', 'started', 'in review'],
    reworkStatuses: ['todo', 'backlog', 'triage', 'in progress'],
    forwardStatuses: ['in review', 'done', 'completed', 'cancelled'],
  },
  monday: {
    name: 'Monday.com',
    accentColor: 'pink',
    doneStatuses: ['done', 'completed', 'closed', 'archived'],
    inProgressStatuses: ['working on it', 'in progress', 'in review'],
    reworkStatuses: ['stuck', 'working on it', 'in progress'],
    forwardStatuses: ['in review', 'done', 'completed', 'closed'],
  },
  trello: {
    name: 'Trello',
    accentColor: 'sky',
    doneStatuses: ['done', 'completed', 'closed', 'archived', 'finished'],
    inProgressStatuses: ['doing', 'in progress', 'in review'],
    reworkStatuses: ['to do', 'blocked', 'doing'],
    forwardStatuses: ['in review', 'done', 'completed', 'finished'],
  },
};

const SEVERITY_COLORS = {
  good: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', text: 'text-emerald-400' },
  warning: { bg: 'bg-amber-500/10', border: 'border-amber-500/20', text: 'text-amber-400' },
  critical: { bg: 'bg-red-500/10', border: 'border-red-500/20', text: 'text-red-400' },
};

// ==================== Indicator Computation ====================

function computeLeadIndicators(events: NormalizedEvent[], config: PlatformConfig): AggregateIndicator[] {
  if (events.length === 0) return [];
  const indicators: AggregateIndicator[] = [];

  // 1. Weekly Throughput
  const weekMap = new Map<string, Set<string>>();
  for (const e of events) {
    const d = new Date(e.timestamp);
    const wk = `${d.getFullYear()}-W${Math.ceil((d.getDate() + new Date(d.getFullYear(), d.getMonth(), 1).getDay()) / 7)}`;
    if (!weekMap.has(wk)) weekMap.set(wk, new Set());
    weekMap.get(wk)!.add(e.itemKey);
  }
  const wAvg = weekMap.size > 0 ? [...weekMap.values()].reduce((s, v) => s + v.size, 0) / weekMap.size : 0;
  indicators.push({
    id: 'lead-throughput',
    name: 'Weekly Throughput',
    label: `${wAvg.toFixed(1)} items/week`,
    severity: wAvg >= 15 ? 'good' : wAvg >= 8 ? 'warning' : 'critical',
    icon: mkIcon(faChartBar),
    baseline: '✓ ≥15/wk | ⚠ 8–15 | ✗ <8',
  });

  // 2. Active Team Members
  const teamSize = new Set(events.map(e => e.author)).size;
  indicators.push({
    id: 'lead-team',
    name: 'Active Team Members',
    label: `${teamSize} member${teamSize !== 1 ? 's' : ''}`,
    severity: teamSize >= 5 ? 'good' : teamSize >= 3 ? 'warning' : 'critical',
    icon: mkIcon(faUsers),
    baseline: '✓ ≥5 | ⚠ 3–4 | ✗ <3',
  });

  // 3. Items Resolved
  const resolved = new Set(
    events.filter(e =>
      e.type === 'status_change' &&
      e.toStatus &&
      config.doneStatuses.some(s => e.toStatus!.toLowerCase().includes(s))
    ).map(e => e.itemKey)
  );
  indicators.push({
    id: 'lead-resolved',
    name: 'Items Resolved',
    label: `${resolved.size} resolved`,
    severity: resolved.size >= 20 ? 'good' : resolved.size >= 8 ? 'warning' : 'critical',
    icon: mkIcon(faBolt),
    baseline: '✓ ≥20 | ⚠ 8–20 | ✗ <8',
  });

  // 4. High Priority Resolution
  const highPriorityTypes = events.filter(e =>
    e.itemType && (e.itemType.toLowerCase() === 'bug' || e.itemType.toLowerCase() === 'hotfix' || e.itemType.toLowerCase() === 'urgent')
  );
  const highPriorityKeys = new Set(highPriorityTypes.map(e => e.itemKey));
  const highResolved = new Set([...resolved].filter(k => highPriorityKeys.has(k)));
  const highResPct = highPriorityKeys.size > 0 ? (highResolved.size / highPriorityKeys.size) * 100 : 0;
  if (highPriorityKeys.size > 0) {
    indicators.push({
      id: 'lead-high-priority',
      name: 'High Priority Resolution',
      label: `${highResPct.toFixed(0)}% (${highResolved.size}/${highPriorityKeys.size})`,
      severity: highResPct >= 80 ? 'good' : highResPct >= 50 ? 'warning' : 'critical',
      icon: mkIcon(faShieldHalved),
      baseline: '✓ ≥80% | ⚠ 50–80% | ✗ <50%',
    });
  }

  // 5. Velocity Trend (items/day trend)
  const dayMap = new Map<string, Set<string>>();
  for (const e of events) {
    const day = e.timestamp.split('T')[0];
    if (!dayMap.has(day)) dayMap.set(day, new Set());
    dayMap.get(day)!.add(e.itemKey);
  }
  const dAvg = dayMap.size > 0 ? [...dayMap.values()].reduce((s, v) => s + v.size, 0) / dayMap.size : 0;
  indicators.push({
    id: 'lead-velocity',
    name: 'Daily Velocity',
    label: `${dAvg.toFixed(1)} items/day`,
    severity: dAvg >= 5 ? 'good' : dAvg >= 2 ? 'warning' : 'critical',
    icon: mkIcon(faChartLine),
    baseline: '✓ ≥5/day | ⚠ 2–5 | ✗ <2',
  });

  return indicators;
}

function computeLagIndicators(events: NormalizedEvent[], config: PlatformConfig): AggregateIndicator[] {
  if (events.length === 0) return [];
  const indicators: AggregateIndicator[] = [];

  // 1. Status Thrashing — items with >3 status changes
  const statusChanges = new Map<string, number>();
  for (const e of events) {
    if (e.type === 'status_change') {
      statusChanges.set(e.itemKey, (statusChanges.get(e.itemKey) || 0) + 1);
    }
  }
  const thrashing = [...statusChanges.entries()].filter(([, c]) => c > 3);
  const thrashPct = statusChanges.size > 0 ? (thrashing.length / statusChanges.size) * 100 : 0;
  indicators.push({
    id: 'lag-thrash',
    name: 'Status Thrashing',
    label: `${thrashing.length} items (${thrashPct.toFixed(0)}%)`,
    severity: thrashPct > 30 ? 'critical' : thrashPct > 15 ? 'warning' : 'good',
    icon: mkIcon(faRotate),
    baseline: '✓ <15% | ⚠ 15–30% | ✗ >30%',
    details: thrashing.slice(0, 5).map(([key, count]) => `${key}: ${count} status changes`),
  });

  // 2. Rework Rate — items moved backward after moving forward
  let reworkCount = 0;
  const issueLastForward = new Map<string, boolean>();
  const sortedEvts = [...events].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
  for (const e of sortedEvts) {
    if (e.type === 'status_change' && e.toStatus) {
      const to = e.toStatus.toLowerCase();
      if (config.forwardStatuses.some(s => to.includes(s))) {
        issueLastForward.set(e.itemKey, true);
      } else if (config.reworkStatuses.some(s => to.includes(s)) && issueLastForward.get(e.itemKey)) {
        reworkCount++;
        issueLastForward.set(e.itemKey, false);
      }
    }
  }
  const uniqueIssueCount = new Set(events.map(e => e.itemKey)).size;
  const reworkPct = uniqueIssueCount > 0 ? (reworkCount / uniqueIssueCount) * 100 : 0;
  indicators.push({
    id: 'lag-rework',
    name: 'Rework Rate',
    label: `${reworkCount} rework event${reworkCount !== 1 ? 's' : ''} (${reworkPct.toFixed(0)}% of items)`,
    severity: reworkPct > 25 ? 'critical' : reworkPct > 10 ? 'warning' : 'good',
    icon: mkIcon(faFire),
    baseline: '✓ <10% | ⚠ 10–25% | ✗ >25%',
  });

  // 3. Unassignment Events
  const unassign = events.filter(e => e.type === 'assignment' && !e.toAssignee).length;
  indicators.push({
    id: 'lag-unassign',
    name: 'Unassignment Events',
    label: `${unassign} unassignment${unassign !== 1 ? 's' : ''}`,
    severity: unassign > 10 ? 'critical' : unassign > 3 ? 'warning' : 'good',
    icon: mkIcon(faTriangleExclamation),
    baseline: '✓ ≤3 | ⚠ 4–10 | ✗ >10',
  });

  // 4. Stale Items — items with last activity >7 days ago (excluding done)
  const now = Date.now();
  const issueLastActivity = new Map<string, number>();
  const issueLastStatus = new Map<string, string>();
  for (const e of events) {
    const ts = new Date(e.timestamp).getTime();
    if (!issueLastActivity.has(e.itemKey) || ts > issueLastActivity.get(e.itemKey)!) {
      issueLastActivity.set(e.itemKey, ts);
    }
    if (e.type === 'status_change' && e.toStatus) {
      if (ts >= (issueLastActivity.get(e.itemKey) || 0)) issueLastStatus.set(e.itemKey, e.toStatus.toLowerCase());
    }
  }
  const openItems = [...issueLastActivity.entries()].filter(([key]) => {
    const lastStatus = issueLastStatus.get(key) || '';
    return !config.doneStatuses.some(s => lastStatus.includes(s));
  });
  const staleItems = openItems.filter(([, ts]) => (now - ts) > 7 * 86400000);
  const stalePct = openItems.length > 0 ? (staleItems.length / openItems.length) * 100 : 0;
  indicators.push({
    id: 'lag-stale',
    name: 'Stale Items',
    label: `${staleItems.length} items (${stalePct.toFixed(0)}%)`,
    severity: stalePct > 40 ? 'critical' : stalePct > 20 ? 'warning' : 'good',
    icon: mkIcon(faClock),
    baseline: '✓ <20% | ⚠ 20–40% | ✗ >40%',
  });

  // 5. Blocked/Stuck ratio
  const blockedEvents = events.filter(e =>
    e.type === 'status_change' &&
    e.toStatus &&
    (e.toStatus.toLowerCase().includes('block') || e.toStatus.toLowerCase().includes('stuck'))
  );
  const blockedKeys = new Set(blockedEvents.map(e => e.itemKey));
  const blockedPct = uniqueIssueCount > 0 ? (blockedKeys.size / uniqueIssueCount) * 100 : 0;
  if (blockedKeys.size > 0) {
    indicators.push({
      id: 'lag-blocked',
      name: 'Blocked Items',
      label: `${blockedKeys.size} items (${blockedPct.toFixed(0)}%)`,
      severity: blockedPct > 20 ? 'critical' : blockedPct > 10 ? 'warning' : 'good',
      icon: mkIcon(faBullseye),
      baseline: '✓ <10% | ⚠ 10–20% | ✗ >20%',
    });
  }

  return indicators;
}

// ==================== Render Helpers ====================

function renderIndicatorTable(
  title: string,
  icon: React.ComponentType<{ className?: string }>,
  items: AggregateIndicator[],
  theme: 'lead' | 'lag'
) {
  if (items.length === 0) return null;
  const Icon = icon;
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <Icon className={`h-3 w-3 ${theme === 'lead' ? 'text-emerald-400' : 'text-red-400'}`} />
        <span className="text-[10px] font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider">{title}</span>
      </div>
      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-slate-700/30">
        <table className="w-full text-[11px] table-fixed">
          <colgroup><col style={{ width: '40%' }} /><col style={{ width: '8%' }} /><col style={{ width: '52%' }} /></colgroup>
          <thead>
            <tr className="bg-gray-50 dark:bg-slate-900/50">
              <th className="text-left px-2 py-1 text-[10px] font-semibold text-gray-500 dark:text-slate-400">Indicator</th>
              <th className="text-center px-1 py-1 text-[10px] font-semibold text-gray-500 dark:text-slate-400"></th>
              <th className="text-right px-2 py-1 text-[10px] font-semibold text-gray-500 dark:text-slate-400">Value</th>
            </tr>
          </thead>
          <tbody>
            {items.map(ind => {
              const colors = SEVERITY_COLORS[ind.severity] || SEVERITY_COLORS.warning;
              const IndIcon = ind.icon;
              return (
                <tr key={ind.id} className="border-t border-gray-100 dark:border-slate-700/15 hover:bg-gray-50 dark:hover:bg-slate-700/10">
                  <td className="px-2 py-1">
                    <div className="flex items-center gap-1.5">
                      <IndIcon className="h-3 w-3 text-gray-400 dark:text-slate-500 flex-shrink-0" />
                      <span className="text-[11px] text-gray-700 dark:text-slate-300 truncate">{ind.name}</span>
                    </div>
                  </td>
                  <td className="px-1 py-1 text-center">
                    <span className={`inline-block w-5 h-5 leading-5 rounded text-[10px] font-bold text-center ${colors.bg} ${colors.text} border ${colors.border}`}>
                      {ind.severity === 'good' ? '✓' : ind.severity === 'warning' ? '⚠' : '✗'}
                    </span>
                  </td>
                  <td className="px-2 py-1 text-right">
                    <div className={`text-[10px] font-mono ${colors.text} truncate`}>{ind.label}</div>
                    <div className="text-[8px] font-mono text-gray-400 dark:text-slate-600 truncate">{ind.baseline}</div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ==================== Main Component ====================

interface PlatformIndicatorsProps {
  events: NormalizedEvent[];
  platform: PlatformType;
  collapsed?: boolean;
}

export function PlatformIndicators({ events, platform, collapsed: initialCollapsed = true }: PlatformIndicatorsProps) {
  const [isCollapsed, setIsCollapsed] = useState(initialCollapsed);
  const config = getPlatformConfig(platform);

  const leadIndicators = useMemo(() => computeLeadIndicators(events, config), [events, config]);
  const lagIndicators = useMemo(() => computeLagIndicators(events, config), [events, config]);

  const totalGood = [...leadIndicators, ...lagIndicators].filter(i => i.severity === 'good').length;
  const totalWarn = [...leadIndicators, ...lagIndicators].filter(i => i.severity === 'warning').length;
  const totalCrit = [...leadIndicators, ...lagIndicators].filter(i => i.severity === 'critical').length;

  if (events.length === 0) return null;

  return (
    <div className="bg-white/60 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/50 rounded-xl overflow-hidden">
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="w-full flex items-center justify-between px-5 py-3 hover:bg-gray-50 dark:hover:bg-slate-700/20 transition-colors"
      >
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faChartBar} className={`h-4 w-4 text-${config.accentColor}-400`} />
          <span className="text-sm font-medium text-gray-900 dark:text-white">{config.name} Lead & Lag Indicators</span>
          <div className="flex items-center gap-1.5 ml-2">
            {totalGood > 0 && (
              <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">
                {totalGood} ✓
              </span>
            )}
            {totalWarn > 0 && (
              <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-amber-500/10 text-amber-500 border border-amber-500/20">
                {totalWarn} ⚠
              </span>
            )}
            {totalCrit > 0 && (
              <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-red-500/10 text-red-500 border border-red-500/20">
                {totalCrit} ✗
              </span>
            )}
          </div>
        </div>
        {isCollapsed ? <FontAwesomeIcon icon={faChevronDown} className="h-4 w-4 text-gray-400 dark:text-slate-400" /> : <FontAwesomeIcon icon={faChevronUp} className="h-4 w-4 text-gray-400 dark:text-slate-400" />}
      </button>

      {!isCollapsed && (
        <div className="px-5 pb-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {renderIndicatorTable(`${config.name} Lead Indicators`, mkIcon(faArrowTrendUp), leadIndicators, 'lead')}
            {renderIndicatorTable(`${config.name} Lag Indicators`, mkIcon(faArrowTrendDown), lagIndicators, 'lag')}
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== Normalization Helpers ====================

export function normalizeLinearEvents(events: Array<{
  id: string;
  timestamp: string;
  type: string;
  issueIdentifier: string;
  issueSummary: string;
  author: { displayName: string };
  fromState?: string;
  toState?: string;
  fromAssignee?: string;
  toAssignee?: string;
}>): NormalizedEvent[] {
  return events.map(e => ({
    id: e.id,
    timestamp: e.timestamp,
    type: e.type === 'state_change' ? 'status_change' : e.type,
    itemKey: e.issueIdentifier,
    itemSummary: e.issueSummary,
    author: e.author.displayName,
    fromStatus: e.fromState,
    toStatus: e.toState,
    fromAssignee: e.fromAssignee,
    toAssignee: e.toAssignee,
  }));
}

export function normalizeMondayEvents(events: Array<{
  id: string;
  timestamp: string;
  type: string;
  itemName: string;
  boardName: string;
  author: { displayName: string };
  fieldName?: string;
  fromValue?: string;
  toValue?: string;
}>): NormalizedEvent[] {
  return events.map(e => ({
    id: e.id,
    timestamp: e.timestamp,
    type: e.fieldName === 'Status' || e.type === 'status_change' ? 'status_change' :
          e.fieldName === 'Person' || e.type === 'assignment' ? 'assignment' : e.type,
    itemKey: e.itemName,
    itemSummary: `${e.boardName} / ${e.itemName}`,
    author: e.author.displayName,
    fromStatus: e.fieldName === 'Status' ? e.fromValue : undefined,
    toStatus: e.fieldName === 'Status' ? e.toValue : undefined,
    fromAssignee: e.fieldName === 'Person' ? e.fromValue : undefined,
    toAssignee: e.fieldName === 'Person' ? e.toValue : undefined,
  }));
}

export function normalizeTrelloEvents(events: Array<{
  id: string;
  timestamp: string;
  type: string;
  cardName: string;
  boardName: string;
  author: { displayName: string };
  listBefore?: string;
  listAfter?: string;
  fieldName?: string;
  fromValue?: string;
  toValue?: string;
}>): NormalizedEvent[] {
  return events.map(e => ({
    id: e.id,
    timestamp: e.timestamp,
    type: e.listBefore || e.listAfter ? 'status_change' :
          e.type === 'assignment' ? 'assignment' : e.type,
    itemKey: e.cardName,
    itemSummary: `${e.boardName} / ${e.cardName}`,
    author: e.author.displayName,
    fromStatus: e.listBefore,
    toStatus: e.listAfter,
    fromAssignee: e.type === 'assignment' ? e.fromValue : undefined,
    toAssignee: e.type === 'assignment' ? e.toValue : undefined,
  }));
}
